import { useCallback, useMemo } from 'react'
import type {
  ExperienceDetailsForm,
} from 'components/Experiences/ExperienceDetailsModal/ExperienceDetailsBookingModal'
import ExperienceDetailsBookingModal from 'components/Experiences/ExperienceDetailsModal/ExperienceDetailsBookingModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { arrayToObject } from 'lib/array/arrayUtils'
import type { ExperienceDetails } from 'lib/checkout/experiences/cart'
import { generateExperienceCheckoutItem } from 'lib/checkout/experiences/cart'
import { useAppDispatch } from 'hooks/reduxHooks'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import type { SavedExperienceData } from 'tripPlanner/types/tripItem'

export interface ExperienceTripPlannerModalResult {
  view: App.ExperienceBookingView
  ticketCounts: Record<string, number>
  params: ExperienceDetailsForm
  tickets: Array<App.ExperienceItemTicket>
}

interface Props {
  experienceView: App.ExperienceBookingView
  savedItemData?: SavedExperienceData
  tripStartDate?: string
  tripEndDate?: string
  propertyLocation: {
    latitude: number | undefined
    longitude: number | undefined
  }
  isLuxPlusEnabled: boolean
}

function ExperienceTripPlannerModal({
  experienceView,
  savedItemData,
  tripStartDate,
  tripEndDate,
  propertyLocation,
  isLuxPlusEnabled,
}: Props) {
  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext<ExperienceTripPlannerModalResult | undefined>()

  const defaultTicketCounts = useMemo(() => {
    return savedItemData?.tickets?.reduce((acc, ticket) => {
      acc[ticket.ticketId] = ticket.count
      return acc
    }, {} as Record<string, number>) ?? {}
  }, [savedItemData])

  const defaultLocation = useMemo(() => {
    if (!savedItemData?.redemptionLocationId) return undefined
    return experienceView.originalExperience.redemptionLocations?.find(
      location => location.id === savedItemData.redemptionLocationId,
    )
  }, [savedItemData, experienceView])

  const onSubmit = useCallback((
    view: App.ExperienceBookingView,
    ticketCounts: Record<string, number>,
    params: ExperienceDetailsForm,
    tickets: Array<App.ExperienceItemTicket>,
  ) => {
    const ticketMap = arrayToObject(tickets, (ticket) => ticket.id)
    const counts = Object.keys(ticketCounts)
      .filter(ticketId => ticketMap[ticketId])
      .map(ticketId => ({
        ticket: ticketMap[ticketId],
        count: ticketCounts[ticketId],
      }))

    const details: ExperienceDetails = {
      ...params,
      redemptionLocation: view.originalExperience.redemptionLocations?.find(location => location.id === params.redemptionLocation)?.id,
      pickupPoint: view.originalExperience.pickupPoints?.find(point => point.id === params.pickupPoint)?.id,
      language: view.originalExperience.languages?.find(lang => lang.id === params.language)?.id,
    }

    const item = generateExperienceCheckoutItem(view.originalExperience, details, counts)
    dispatch(initialiseCheckout([item]))

    dispatch(navigateToCheckout())
  }, [dispatch])

  const onClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  return <ExperienceDetailsBookingModal
    experienceView={experienceView}
    checkInDate={tripStartDate}
    checkOutDate={tripEndDate}
    defaultDate={savedItemData?.date}
    defaultTimeSlot={savedItemData?.time}
    defaultTicketCounts={defaultTicketCounts}
    defaultLocation={defaultLocation}
    defaultTicketModesMap={{}}
    currencyCode={savedItemData?.currencyCode}
    disableRemove
    onSubmit={onSubmit}
    onClose={onClose}
    propertyLocation={propertyLocation}
    isLuxPlusEnabled={isLuxPlusEnabled}
    ctaText="Buy now"
  />
}

export default ExperienceTripPlannerModal
